import React from 'react';
import { styled, css, Box, Flex, Text } from '@goodrx/matisse';
import { Message } from '@goodrx/heydoctor-sdk';

import { useAppContext } from 'app/config/AppContext';
import { Timestamp } from 'app/components/core/Timestamp';
import { ProviderModal } from 'app/components/widgets/chat/modals';

const UnderlineOnHover = styled.div<{ disabled?: boolean }>`
  ${(p) =>
    !p.disabled &&
    css`
      :hover {
        text-decoration: underline;
      }
    `}
`;

interface MessageTitleProps {
  name: string;
  createdAt: string;
  underlineOnHover?: boolean;
  isGold?: boolean;
}

const MessageTitle: React.FC<MessageTitleProps> = ({ name, createdAt, underlineOnHover, isGold = false }) => (
  <Flex ml={isGold ? 1 : 3} mb={1} alignItems="flex-end">
    <UnderlineOnHover disabled={!underlineOnHover}>
      <Text fontWeight={isGold ? 400 : 500} fontSize={isGold ? 13 : 14} color={isGold ? 'greyDarker' : 'greyDark'}>
        {name}
      </Text>
    </UnderlineOnHover>
    {!isGold && <Timestamp color="greyDarker" ml={1} fontSize={14} date={createdAt} format="h:mma" />}
  </Flex>
);

interface MessageSignatureProps {
  message: Message;
  fromCurrentUser: boolean;
}

export const MessageSignature: React.FC<MessageSignatureProps> = ({
  message: { from, createdAt },
  fromCurrentUser,
}) => {
  const { isGold } = useAppContext();
  const name = `${from.firstName} ${from.lastName}${from.displayName ? `, ${from.displayName}` : ''}`;
  const titleProps = { createdAt, name, isGold };
  return (
    <>
      {!fromCurrentUser &&
        (from.provider?.npi ? (
          <ProviderModal
            name={name}
            npi={from.provider.npi}
            trigger={
              <div>
                <MessageTitle {...titleProps} underlineOnHover />
              </div>
            }
          />
        ) : (
          <>
            <MessageTitle {...titleProps} />
            {isGold && (
              <Box textAlign="left">
                <Timestamp
                  data-qa="message_timestamp"
                  color="greyDarker"
                  ml={1}
                  fontSize={14}
                  mr={3}
                  date={createdAt}
                  format="MMMM Do, h:mm a"
                />
              </Box>
            )}
          </>
        ))}

      {fromCurrentUser && (
        <Box textAlign="right">
          <Timestamp
            data-qa="message_timestamp"
            color="greyDarker"
            ml={1}
            fontSize={14}
            mr={3}
            date={createdAt}
            format={isGold ? 'MMMM Do, h:mm a' : 'h:mma'}
          />
        </Box>
      )}
    </>
  );
};
